import { createContext, useContext, useEffect, useMemo, useSyncExternalStore, type ReactNode } from "react";
import { useClient, type Client, type PlaceFacts } from "../../client/index.ts";
import type { Find } from "./pinch.ts";
import { createNavigator, kindBetween, localUrl, type HistoryPort, type NavKind, type Routes, type Visit } from "./router.ts";
import { focus, levelElement, open, pathOf, show, toHash, zoom, type Level, type Route, type Stage } from "./stage.ts";
import { arrive, watchPinch } from "./zoom.ts";

interface Navigation {
  readonly route: Route;
  readonly levels: readonly Level[];
  readonly routes: Routes;
  go(href: string, options?: { readonly kind?: NavKind | undefined; readonly origin?: Element | null }): void;
  back(): void;
  zoomOut(): void;
  sibling(direction: "prev" | "next"): void;
  redirect(href: string): void;
  cleanQuery(): void;
  facts(): PlaceFacts | undefined;
  prefetch(href: string): void;
  pageIn(): void;
}

type Moves = Omit<Navigation, "route" | "levels"> & { readonly stage: Stage; readonly subscribe: (listener: () => void) => () => void };
const NavigationContext = createContext<Moves | null>(null);

export function useNavigation(): Navigation {
  const moves = useContext(NavigationContext);
  if (!moves) throw new Error("useNavigation ran outside NavigationProvider");
  const shown = useSyncExternalStore(moves.subscribe, () => moves.stage.shown);
  return useMemo<Navigation>(() => {
    const levels = [shown.here, ...(shown.other ? [shown.other] : [])].sort((a, b) => a.id - b.id);
    return { ...moves, route: shown.here.route, levels };
  }, [moves, shown]);
}

const browserHistory: HistoryPort = {
  url: () => `${location.pathname}${location.search}`,
  state: () => history.state,
  push: (url, state) => history.pushState(state, "", url),
  replace: (url, state) => history.replaceState(state, "", url),
  back: () => history.back(),
};

const patience = 120;
const prefetchAfter = 80;

function leave(stage: Stage, url?: string): number {
  if (stage.flying?.url !== url) stage.flying?.settle();
  return scrollY;
}

async function render(stage: Stage, visit: Visit, origin?: Element | null, hash = ""): Promise<void> {
  const token = ++stage.latest;
  stage.owed = null;
  if (stage.flying?.url === visit.url) return stage.flying.land();
  const next = open(stage.routes, visit.url);
  const loading = next.opened?.load(stage.client).catch(() => undefined);
  await Promise.race([loading, new Promise((resolve) => setTimeout(resolve, patience))]);
  if (token !== stage.latest) return;
  stage.flying?.settle();
  const here = stage.shown.here;
  if (pathOf(here.route.url) === pathOf(visit.url)) {
    show(stage, { here: { ...here, route: next } });
    if (hash) toHash(document, hash);
    else if (visit.scroll !== undefined) scrollTo(0, visit.scroll);
    return;
  }
  const zooms = visit.kind === "zoom-in" || visit.kind === "zoom-out";
  const flight = zooms ? zoom(stage, next, visit.kind === "zoom-in", origin, visit.scroll ?? 0, hash) : null;
  if (flight) return flight.land();
  const level: Level = { id: ++stage.ids, route: next };
  show(stage, { here: level });
  scrollTo(0, visit.scroll ?? 0);
  const arrived = levelElement(level.id);
  if (arrived) arrive(arrived, visit.kind === "sibling" ? (visit.back ? "prev" : "next") : undefined);
  focus(stage, visit.kind, visit.back, here.route, hash);
}

const go = (stage: Stage): Navigation["go"] => (href, options = {}) => {
  const visit = stage.navigator.go(href, leave(stage, localUrl(href)), options.kind);
  if (visit) void render(stage, visit, options.origin, new URL(href, location.origin).hash);
};

const factsOf = (stage: Stage) => stage.shown.here.route.opened?.peek(stage.client);
const parentOf = (stage: Stage) => factsOf(stage)?.parent ?? factsOf(stage)?.path.at(-1)?.href;

function zoomOut(stage: Stage): void {
  const href = parentOf(stage);
  if (href === undefined) return;
  const visit = stage.navigator.zoomOut(href, leave(stage, localUrl(href)));
  if (visit) void render(stage, visit);
}

function kindOf(stage: Stage, link: HTMLAnchorElement): NavKind | undefined {
  const said = link.dataset["nav"];
  if (isKind(said)) return said;
  const to = stage.routes.match(link.href);
  if (!to || !link.closest("[data-palco]") || link.pathname === location.pathname) return undefined;
  return kindBetween(stage.shown.here.route.match?.entry.level, to.entry.level, true);
}

function linkAround(stage: Stage, target: Element): HTMLAnchorElement | null {
  for (let at: Element | null = target; at; at = at.parentElement) {
    const cells = at.matches("tr, li") && !at.hasAttribute("data-zoom-origin") ? at.querySelectorAll("[data-zoom-origin]") : [];
    const card = (cells.length === 1 && cells[0]) || at;
    const mark = card.getAttribute("data-zoom-origin");
    const links = at instanceof HTMLAnchorElement ? [at] : mark === null ? [] : [...card.querySelectorAll("a")];
    const leads = (link: HTMLAnchorElement) => link === at || open(stage.routes, link.href).opened?.morph === mark;
    const found = links.find((link) => kindOf(stage, link) === "zoom-in" && leads(link));
    if (found) return found;
  }
  return null;
}

const find = (stage: Stage): Find<Element> => (direction, target) => {
  if (stage.flying) return "ocupado";
  const link = direction === "in" ? linkAround(stage, target) : null;
  const href = link?.href ?? (direction === "out" ? parentOf(stage) : undefined);
  if (href === undefined) return null;
  const next = open(stage.routes, localUrl(href));
  return {
    hold() {
      void next.opened?.load(stage.client).catch(() => undefined);
      return zoom(stage, next, direction === "in", link, link ? 0 : stage.navigator.scrollOf(href));
    },
    go: () => go(stage)(href, { origin: link, kind: link ? "zoom-in" : "zoom-out" }),
  };
};

function movesOf(stage: Stage): Omit<Navigation, "route" | "levels"> {
  return {
    routes: stage.routes,
    go: go(stage),
    back: () => void stage.navigator.back(leave(stage)),
    zoomOut: () => zoomOut(stage),
    sibling(direction) {
      const target = factsOf(stage)?.siblings?.[direction];
      if (!target) return;
      const visit = stage.navigator.go(target.href, leave(stage), "sibling");
      if (visit) void render(stage, { ...visit, back: direction === "prev" });
    },
    redirect(href) {
      const visit = stage.navigator.redirect(href);
      if (visit) void render(stage, visit);
    },
    cleanQuery: () => void stage.navigator.redirect(location.pathname),
    facts: () => factsOf(stage),
    prefetch: (href) => void open(stage.routes, localUrl(href)).opened?.load(stage.client).catch(() => undefined),
    pageIn() {
      const place = stage.owed;
      stage.owed = null;
      place?.();
    },
  };
}

function listen(stage: Stage): () => void {
  history.scrollRestoration = "manual";
  const popped = () => void render(stage, stage.navigator.popped(leave(stage, browserHistory.url())));
  const clicked = (event: MouseEvent) => {
    const link = followable(event, stage.routes);
    if (!link) return;
    const next = stage.routes.match(link.href);
    if (stage.shown.here.route.match?.entry.public && next?.entry.public !== true) return;
    event.preventDefault();
    go(stage)(link.href, { origin: link, kind: kindOf(stage, link) });
  };
  addEventListener("popstate", popped);
  document.addEventListener("click", clicked);
  const unwatch = watchPinch(find(stage));
  return () => {
    removeEventListener("popstate", popped);
    document.removeEventListener("click", clicked);
    unwatch();
  };
}

export function NavigationProvider({ routes, children }: { readonly routes: Routes; readonly children: ReactNode }) {
  const client = useClient();
  const stage = useMemo<Stage>(() => {
    const navigator = createNavigator(browserHistory, routes);
    const here = { id: 0, route: open(routes, navigator.start().url) };
    return { client, routes, navigator, shown: { here }, ids: 0, latest: 0, flying: null, owed: null, listeners: new Set() };
  }, [client, routes]);
  const moves = useMemo<Moves>(() => {
    const subscribe = (listener: () => void) => {
      stage.listeners.add(listener);
      return () => void stage.listeners.delete(listener);
    };
    return { ...movesOf(stage), stage, subscribe };
  }, [stage]);
  useEffect(() => listen(stage), [stage]);
  usePrefetch(client, routes);
  return <NavigationContext.Provider value={moves}>{children}</NavigationContext.Provider>;
}

const kinds: readonly NavKind[] = ["zoom-in", "zoom-out", "sibling", "jump"];
function isKind(value: string | undefined): value is NavKind {
  return kinds.some((kind) => kind === value);
}

function followable(event: MouseEvent, routes: Routes): HTMLAnchorElement | undefined {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return undefined;
  const link = event.target instanceof Element ? event.target.closest("a[href]") : null;
  if (!(link instanceof HTMLAnchorElement) || link.target === "_blank" || link.hasAttribute("download") || link.origin !== location.origin) return undefined;
  if (link.hash !== "" && link.pathname === location.pathname && link.search === location.search) return undefined;
  return routes.match(link.href) ? link : undefined;
}

function usePrefetch(client: Client, routes: Routes): void {
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const fetched = new Set<string>();
    const aim = (event: Event) => {
      const link = event.target instanceof Element ? event.target.closest("a[href]") : null;
      if (!(link instanceof HTMLAnchorElement) || link.origin !== location.origin) return;
      clearTimeout(timer);
      timer = setTimeout(() => {
        const url = localUrl(link.href);
        if (fetched.has(url)) return;
        fetched.add(url);
        setTimeout(() => fetched.delete(url), 10_000);
        void open(routes, url).opened?.load(client).catch(() => fetched.delete(url));
      }, prefetchAfter);
    };
    const leave = () => clearTimeout(timer);
    document.addEventListener("pointerover", aim);
    document.addEventListener("focusin", aim);
    document.addEventListener("pointerout", leave);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("pointerover", aim);
      document.removeEventListener("focusin", aim);
      document.removeEventListener("pointerout", leave);
    };
  }, [client, routes]);
}
